// src/App.tsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import './styles/themes.css';
import './styles/utilities.css';
import Layout from './components/Layout';
import ToastContainer from './components/ToastContainer';
import QuickActionsModal from './components/QuickActionsModal';
import TurnstileWidget from './components/TurnstileWidget';
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import Settings from './pages/Settings';
import Login from './pages/Login';
import backgroundImage from './assets/SC_25_Wallpaper_3840x2160.jpg';
import Home from './pages/Home';
import Register from './pages/Register';
import Events from './pages/Events';
import LeaderDashboard from './pages/LeaderDashboard';
import PermissionRequests from './pages/PermissionRequests';
import AboutUs from './pages/AboutUs';
import PageTemplate from './pages/PageTemplate';
import { LeaderViewProvider } from './context/LeaderViewContext';
import DepartmentsLanding from './pages/DepartmentsLanding';
import DataRequest from './pages/DataRequest';
import Changelog from './pages/Changelog';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import CreateEvent from './pages/CreateEvent';
import EventDetail from './pages/EventDetail';
import MedicalSOS from './pages/MedicalSOS';
import Posts from './pages/Posts';
// 🆕 Add these imports:
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import MembersDirectory from './pages/MembersDirectory';
import LeadershipGuide from './pages/LeadershipGuide';
import MissionsBoard from './pages/MissionsBoard';
import MyMissions from './pages/MyMissions';
import LeaderMissions from './pages/LeaderMissions';
import UserManagement from './pages/UserManagement';
import MetricsDashboard from './pages/MetricsDashboard';
import PostsManagement from './pages/PostsManagement';
import GeneralOrgPoints from './pages/GeneralOrgPoints';
import OrgPointsHistory from './pages/OrgPointsHistory';
import PositionsManagement from './pages/PositionsManagement';
import PositionApplications from './pages/PositionApplications';
import DepartmentManagement from './pages/DepartmentManagement';
import LeadershipInbox from './pages/LeadershipInbox';
import ComingSoon from './pages/ComingSoon';
import ShipAdvisor from './pages/ShipAdvisor';
import Recognition from './pages/Recognition';
import { AwardCommendation } from './components/AwardCommendation';
import AfterActionReports from './pages/AfterActionReports';
import AARForm from './pages/AARForm';
import AARView from './pages/AARView';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeArticle from './pages/KnowledgeArticle';
import KnowledgeAdmin from './pages/KnowledgeAdmin';
import FeedbackManagement from './pages/FeedbackManagement';
import MyFeedback from './pages/MyFeedback';
import ChatPage from './pages/ChatPage';
import ShipDatabase from './pages/ShipDatabase';
import ShipDetailV2 from './pages/ShipDetailV2';
import ShipDataManager from './pages/ShipDataManager';
import ItemDatabase from './pages/ItemDatabase';
import LoadoutBuilder from './pages/LoadoutBuilder';
import CraftingBlueprints from './pages/CraftingBlueprints';
import ShipRecommender from './pages/ShipRecommender';
import UserFleet from './pages/UserFleet';
import OrgFleet from './pages/OrgFleet';
import OrgFleetAudit from './pages/OrgFleetAudit';
import OrgFleetMembers from './pages/OrgFleetMembers';
import FleetImportGuide from './pages/FleetImportGuide';

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);

  // Global keyboard shortcut listener (CMD/CTRL + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsQuickActionsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  const backgroundStyle = {
    background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6)), url(${backgroundImage}) no-repeat center center fixed`,
    backgroundSize: '130% auto',
    minHeight: '100vh',
    backgroundBlendMode: 'multiply'
  };

  return (
    <ThemeProvider>
      <ToastProvider>
        <div style={backgroundStyle}>
          {/* 🛡️ Invisible Turnstile bot protection */}
          <TurnstileWidget />
          <ToastContainer />
          <QuickActionsModal 
            isOpen={isQuickActionsOpen}
            onClose={() => setIsQuickActionsOpen(false)}
            userRole={user?.role}
            userFlags={user?.flags || []}
          />
          <LeaderViewProvider>
            <Layout onOpenQuickActions={() => setIsQuickActionsOpen(true)}>
            <Routes>
            {user ? (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/home" element={<Home />} />
                <Route path="/aboutus" element={<AboutUs />} />
                <Route path="/departments" element={<DepartmentsLanding />} />
                <Route path="/knowledge" element={<KnowledgeBase />} />
                <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
                <Route path="/knowledge/admin" element={<KnowledgeAdmin />} />
                <Route path="/knowledge/admin/:id" element={<KnowledgeAdmin />} />
                <Route path="/my-feedback" element={<MyFeedback />} />
                <Route path="/my-fleet" element={<UserFleet />} />
                <Route path="/org-fleet" element={<OrgFleetMembers />} />
                <Route path="/fleet-import-guide" element={<FleetImportGuide />} />
                <Route path="/feedback-management" element={<FeedbackManagement />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/ship-advisor" element={<ShipAdvisor />} />
                <Route path="/ship-database" element={<ShipDatabase />} />
                <Route path="/ship-database/:slug" element={<ShipDetailV2 />} />
                <Route path="/ship-guide" element={<Navigate to="/coming-soon" replace />} />
                <Route path="/loadout-builder" element={<LoadoutBuilder />} />
                {/* Legacy redirect */}
                <Route path="/ships" element={<Navigate to="/ship-database" />} />
                <Route path="/ships/:slug" element={<Navigate to="/ship-database" />} />
                <Route path="/items" element={<ItemDatabase />} />
                <Route path="/crafting-blueprints" element={<CraftingBlueprints />} />
                <Route path="/admin/ship-data" element={<ShipDataManager />} />
                
                {user.flags?.includes("OrgMember") && (
                  <>
                    <Route path="/events" element={<Events />} />
                    <Route path="/events/create" element={<CreateEvent />} />
                    <Route path="/events/:id/edit" element={<CreateEvent />} />
                    <Route path="/events/:id" element={<EventDetail />} />
                    <Route path="/members" element={<MembersDirectory />} />
                    <Route path="/recognition" element={<Recognition />} />
                    <Route path="/missions" element={<MissionsBoard />} />
                    <Route path="/my-missions" element={<MyMissions />} />
                  </>
                )}
  
                <Route path="/leader-dashboard" element={<LeaderDashboard />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/metrics-dashboard" element={<MetricsDashboard />} />
                <Route path="/posts-management" element={<PostsManagement />} />
                <Route path="/positions-management" element={<PositionsManagement />} />
                <Route path="/position-applications" element={<PositionApplications />} />
                <Route path="/award-commendation" element={<AwardCommendation />} />
                <Route path="/departments/:department/manage" element={<DepartmentManagement />} />
                <Route path="/leader-dashboard/missions" element={<LeaderMissions />} />
                <Route path="/leader-dashboard/permission-requests" element={<PermissionRequests />} />
                <Route path="/leader-dashboard/org-fleet" element={<OrgFleet />} />
                <Route path="/leader-dashboard/org-fleet/audit" element={<OrgFleetAudit />} />
                <Route path="/leadership-guide" element={<LeadershipGuide />} />
                <Route path="/leadership-inbox" element={<LeadershipInbox />} />
                <Route path="/medical-sos" element={<MedicalSOS />} />
                <Route path="/after-action-reports" element={<AfterActionReports />} />
                <Route path="/after-action-reports/create/:eventId" element={<AARForm />} />
                <Route path="/after-action-reports/edit/:aarId" element={<AARForm />} />
                <Route path="/after-action-reports/view/:aarId" element={<AARView />} />
                <Route path="/posts" element={<Posts />} />
                <Route path="/general/org-points" element={<GeneralOrgPoints />} />
                <Route path="/org-points/history" element={<OrgPointsHistory />} />
                <Route path="/coming-soon" element={<ComingSoon />} />
  
                {/* Dynamic + Static Pages */}
                <Route path="/page/:name" element={<PageTemplate />} />
                <Route path="/mission" element={<PageTemplate pageName="Mission" pageTitle="Our Mission" />} />
                <Route path="/history" element={<PageTemplate pageName="History" pageTitle="Our History" />} />
                <Route path="/vision" element={<PageTemplate pageName="Vision" pageTitle="Our Vision" />} />

                {/* 🆕 Legal Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/data-request" element={<DataRequest />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* fallback */}
                <Route path="*" element={<Navigate to="/home" />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/home" element={<Home />} />
                <Route path="/aboutus" element={<AboutUs />} />
                <Route path="/departments" element={<DepartmentsLanding />} />
                <Route path="/posts" element={<Posts />} />
                <Route path="/knowledge" element={<KnowledgeBase />} />
                <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
                <Route path="/register" element={<Register />} />
                <Route path="/page/:name" element={<PageTemplate />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/data-request" element={<DataRequest />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/general/org-points" element={<GeneralOrgPoints />} />
                <Route path="/ship-advisor" element={<ShipAdvisor />} />
                <Route path="/ship-database" element={<ShipDatabase />} />
                <Route path="/ship-database/:slug" element={<ShipDetailV2 />} />
                <Route path="/ship-guide" element={<Navigate to="/coming-soon" replace />} />
                <Route path="/loadout-builder" element={<LoadoutBuilder />} />
                <Route path="/crafting-blueprints" element={<CraftingBlueprints />} />
                {/* Legacy redirect */}
                <Route path="/ships" element={<Navigate to="/ship-database" />} />
                <Route path="/ships/:slug" element={<Navigate to="/ship-database" />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/coming-soon" element={<ComingSoon />} />
                <Route path="*" element={<Navigate to="/home" />} />
              </>
            )}
          </Routes>
        </Layout>
      </LeaderViewProvider>
    </div>
    </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
